import {
  getRetryDelayMs,
  isReauthInProgress,
  isRetryableApiError,
  parseApiError
} from "@appretio/api";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import type { SuccessToast } from "./tanstack-query";

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/** What the app shows for a failed query or mutation. */
export interface AppErrorToast {
  title?: string;
  description?: string;
}

/**
 * The app's single QueryClient: shared query defaults plus the global
 * error/success toast handlers. Built by a factory (not inline in `App`) so
 * the handlers themselves are testable — the opt-out below is behaviour a
 * page depends on, not configuration.
 */
export function createAppQueryClient(
  showErrorToast: (error: AppErrorToast) => void,
  showSuccessToast: (toast: SuccessToast) => void
): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Deliberately NO global `placeholderData: (prev) => prev`:
        // it made every query render the PREVIOUS key's data while a
        // new key fetched — i.e. another context's data briefly shown
        // as current. Queries where keeping the previous result is
        // desirable (option lists, search-as-you-type) opt in locally
        // with `placeholderData: keepPreviousData`.
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
        // Only the API's retryable-backpressure 503 is retried, honoring
        // its Retry-After. Mutations keep the no-retry default — they are
        // not known to be idempotent.
        // Three attempts: a 503 from a lost cache lock means someone else
        // is hydrating, and each attempt waits up to
        // LOCK_ACQUIRE_TIMEOUT_SECONDS (240 s) for it. Two attempts cover
        // 480 s, short of the 600 s worst-case COPY INTO; three cover it.
        retry: (failureCount, error) =>
          failureCount < 3 && isRetryableApiError(error),
        retryDelay: getRetryDelayMs
      }
    },
    queryCache: new QueryCache({
      onError: async (error, query) => {
        if (isReauthInProgress(error)) return; // redirecting, not failing
        // Opt-out for queries whose consumer renders the failure itself:
        // the toast never dismisses, so it would sit on top of that
        // screen. See `.claude/rules/tanstack-query.md`.
        const suppress = query.meta?.suppressErrorToast;
        if (suppress === true) return;
        if (suppress === "firstLoad" && query.state.data === undefined) return;
        showErrorToast(parseApiError(error, query.meta?.errorMessage));
      }
    }),
    mutationCache: new MutationCache({
      onError: async (error, _variables, _context, mutation) => {
        if (isReauthInProgress(error)) return; // redirecting, not failing
        // The same opt-out as the query cache above.
        if (mutation.meta?.suppressErrorToast) return;
        showErrorToast(
          parseApiError(
            error,
            mutation.meta?.errorMessage as string | undefined
          )
        );
      },
      onSuccess: (data, _variables, _context, mutation) => {
        // The function form builds the toast from the result, or returns
        // nothing to stay silent: the async triggers resolve 200 with
        // `claimed: false` when a run is already in flight.
        const message = mutation.meta?.successMessage as
          | string
          | ((data: unknown) => SuccessToast | string | undefined)
          | undefined;
        const toast = typeof message === "function" ? message(data) : message;
        if (!toast) return;
        showSuccessToast(typeof toast === "string" ? { title: toast } : toast);
      }
    })
  });
}
